import th from '@tillhub/javascript-sdk'
import type { AxiosInstance } from 'axios'
import { env } from '@shared/config/env'
import type {
  ApiErrorBody,
  Catalogue,
  ConversationEventData,
  DoneEventData,
  StatusEventData,
  StreamEvent,
  StreamEventName,
  TextEventData,
  ToolCallResultEventData,
  ToolCallStartEventData,
  TurnError,
  WidgetEventData
} from '../types'
import { createSseParser } from './sse-parser'
import type { SseFrame } from './sse-parser'

/*
 * The Dashboard's client for the agent-chat API (merchant-intelligence-service), ported from
 * merchant-agent-sample-app. Requests go through the SDK's axios client, so they carry its credentials and pass its
 * interceptors. Streams use axios' fetch adapter + an SSE parser + AbortController, never EventSource (it cannot
 * POST or send Authorization), and never reconnect: a resend re-runs the turn.
 */

export class ApiError extends Error {
  status: number
  /* the boilerplate error envelope { status, msg, errorCode? } when the service sent one */
  body: ApiErrorBody | undefined

  constructor(status: number, body?: ApiErrorBody) {
    super(body?.msg ?? `HTTP ${status}`)
    this.name = 'ApiError'
    this.status = status
    this.body = body
  }
}

/* a frame of the turn stream whose `data:` is not JSON or lacks its event's fields, e.g. `data: <html>` */
export class MalformedEventError extends Error {
  event: string

  constructor(event: string) {
    super(`Malformed "${event}" event in the agent-chat stream`)
    this.name = 'MalformedEventError'
    this.event = event
  }
}

export const apiBase = (): string =>
  (env.agentChatApiBase || '').replace(/\/$/, '')

/*
 * The SDK's axios client, whose defaults carry `Authorization` and, on a Keycloak session, `x-whitelabel`. The
 * Dashboard re-initialises the SDK on login, token refresh and 2FA downgrade, so reading it per request stays current.
 */
const sdkClient = (): AxiosInstance => {
  const client = th.http?.getClient()
  if (!client?.defaults.headers.common?.Authorization) {
    throw new ApiError(401, { msg: 'No Dashboard session' })
  }
  return client
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value)

/* the envelope's known fields; a body that is not an object is no envelope */
const toErrorBody = (value: unknown): ApiErrorBody | undefined => {
  if (!isRecord(value)) return undefined
  const { status, msg, errorCode } = value
  return {
    ...(typeof status === 'number' && { status }),
    ...(typeof msg === 'string' && { msg }),
    ...(typeof errorCode === 'string' && { errorCode })
  }
}

/* a streamed response's error body is still a ReadableStream; anything not JSON is no envelope */
const errorBody = async (data: unknown): Promise<ApiErrorBody | undefined> => {
  if (!(data instanceof ReadableStream)) return toErrorBody(data)
  const parsed: unknown = await new Response(data).json().catch(() => undefined)
  return toErrorBody(parsed)
}

interface ErrorWithResponse {
  response: { status: number; data?: unknown }
}

const hasResponse = (error: unknown): error is ErrorWithResponse =>
  isRecord(error) &&
  isRecord(error.response) &&
  typeof error.response.status === 'number'

/* turns an axios error with a response into an ApiError; network errors pass through */
const toApiError = async (error: unknown): Promise<unknown> => {
  if (!hasResponse(error)) return error
  return new ApiError(
    error.response.status,
    await errorBody(error.response.data)
  )
}

type EventData = Record<string, unknown>

const isString = (value: unknown): value is string => typeof value === 'string'

/* the fields the drawer reads without a fallback; the rest of each shape is the contract's */
const isConversationData = (
  data: EventData
): data is EventData & ConversationEventData => isString(data.conversation_id)

const isStatusData = (data: EventData): data is EventData & StatusEventData =>
  isString(data.stage)

const isToolCallStartData = (
  data: EventData
): data is EventData & ToolCallStartEventData =>
  isString(data.call_id) && isString(data.name)

const isToolCallResultData = (
  data: EventData
): data is EventData & ToolCallResultEventData =>
  isString(data.call_id) && isString(data.name) && isString(data.status)

const isTextData = (data: EventData): data is EventData & TextEventData =>
  isString(data.delta)

const isWidgetData = (data: EventData): data is EventData & WidgetEventData =>
  isString(data.widget_id) && isString(data.kind)

const isDoneData = (data: EventData): data is EventData & DoneEventData =>
  isString(data.status)

const isErrorData = (data: EventData): data is EventData & TurnError =>
  isString(data.code)

/* per contract event, the typed event when `data` has that event's shape, else undefined */
const EVENT_PARSERS: Record<
  StreamEventName,
  (data: EventData) => StreamEvent | undefined
> = {
  conversation: (data) =>
    isConversationData(data) ? { event: 'conversation', data } : undefined,
  status: (data) =>
    isStatusData(data) ? { event: 'status', data } : undefined,
  tool_call_start: (data) =>
    isToolCallStartData(data) ? { event: 'tool_call_start', data } : undefined,
  tool_call_result: (data) =>
    isToolCallResultData(data)
      ? { event: 'tool_call_result', data }
      : undefined,
  text: (data) => (isTextData(data) ? { event: 'text', data } : undefined),
  widget: (data) =>
    isWidgetData(data) ? { event: 'widget', data } : undefined,
  done: (data) => (isDoneData(data) ? { event: 'done', data } : undefined),
  error: (data) => (isErrorData(data) ? { event: 'error', data } : undefined)
}

const isStreamEventName = (event: string): event is StreamEventName =>
  Object.hasOwn(EVENT_PARSERS, event)

/* the event of a frame whose data has its event's shape; undefined for an event name the contract does not have */
const toStreamEvent = (
  event: string,
  data: EventData
): StreamEvent | undefined => {
  if (!isStreamEventName(event)) return undefined
  const streamEvent = EVENT_PARSERS[event](data)
  if (!streamEvent) throw new MalformedEventError(event)
  return streamEvent
}

/* the parsed event of one frame; a frame whose data is not JSON, or lacks its event's fields, breaks the contract */
const parseEvent = ({ event, data }: SseFrame): StreamEvent | undefined => {
  let parsed: unknown
  try {
    parsed = JSON.parse(data)
  } catch {
    throw new MalformedEventError(event)
  }
  if (!isRecord(parsed)) throw new MalformedEventError(event)
  return toStreamEvent(event, parsed)
}

/* GET /api/v0/agent-chat/{tenantId}/catalogue → { version, capabilityText, prompts, tools } */
export async function fetchCatalogue({
  tenantId
}: {
  tenantId: string
}): Promise<Catalogue> {
  const client = sdkClient()
  try {
    const { data } = await client.get<Catalogue>(
      `${apiBase()}/api/v0/agent-chat/${tenantId}/catalogue`,
      { headers: { accept: 'application/json' }, skipErrorRedirect: true }
    )
    return data
  } catch (error: unknown) {
    throw await toApiError(error)
  }
}

export interface StreamTurnOptions {
  tenantId: string
  conversationId?: string
  prompt: string
  /* the dashboard locale the answer is written in, e.g. 'de-DE' */
  locale?: string
  signal?: AbortSignal
  onEvent: (event: StreamEvent) => void
}

/**
 * Runs one turn: POST /api/v0/agent-chat/{tenantId}[/{conversationId}] and calls
 * `onEvent({ event, data })` for every SSE frame of a contract event, with `data`
 * parsed from JSON; frames of other event names are skipped. Resolves when the
 * server closes the stream; throws ApiError when a gate refuses before
 * streaming, MalformedEventError when a frame breaks the contract, and an
 * AbortError when `signal` aborts.
 */
export async function streamTurn({
  tenantId,
  conversationId,
  prompt,
  locale,
  signal,
  onEvent
}: StreamTurnOptions): Promise<void> {
  const client = sdkClient()
  const conversationPath = conversationId ? `/${conversationId}` : ''
  const path = `/api/v0/agent-chat/${tenantId}${conversationPath}`
  let reader: ReadableStreamDefaultReader<string> | undefined
  try {
    const response = await client.post<
      ReadableStream<ArrayBufferView | ArrayBuffer> | undefined
    >(
      `${apiBase()}${path}`,
      { prompt, locale },
      {
        /* only the fetch adapter hands over the body as a ReadableStream while it arrives */
        adapter: 'fetch',
        responseType: 'stream',
        /* a turn streams for as long as the agent works; the SDK's 10 s default would cut it off */
        timeout: 0,
        headers: { accept: 'text/event-stream' },
        /* a refused turn or a timeout is shown in the drawer, not as the /403 or /504 page */
        skipErrorRedirect: true,
        signal
      }
    )
    if (!response.data) throw new ApiError(response.status)

    const parser = createSseParser({
      onEvent: (frame) => {
        const event = parseEvent(frame)
        if (event) onEvent(event)
      }
    })
    const streamReader = response.data
      .pipeThrough(new TextDecoderStream())
      .getReader()
    reader = streamReader
    for (;;) {
      const { done, value } = await streamReader.read()
      if (done) break
      parser.feed(value)
    }
  } catch (error: unknown) {
    /* axios rejects an abort with a CanceledError, before and during the stream */
    if (signal?.aborted) throw new DOMException('Turn aborted', 'AbortError')
    /* a malformed frame ends the turn; the rest of the response is not read */
    if (error instanceof MalformedEventError) {
      reader?.cancel().catch(() => undefined)
      throw error
    }
    throw await toApiError(error)
  }
}
